import api from './api';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// // Create axios instance with default config
// const api = axios.create({
//   baseURL: API_URL,
//   withCredentials: true, // Important for session cookies
//   headers: {
//     'Content-Type': 'application/json',
//     'Accept': 'application/json',
//   },
// });

// // Request interceptor to add auth token if available
// api.interceptors.request.use(
//   (config) => {
//     // Add any auth headers here if needed
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

// // Response interceptor to handle common errors
// api.interceptors.response.use(
//   (response) => {
//     return response;
//   },
//   (error) => {
//     if (error.response?.status === 401) {
//       // Handle unauthorized access
//       localStorage.removeItem('user');
//       localStorage.removeItem('isAuthenticated');
//       // Don't redirect here - let the app handle it
//     }
//     return Promise.reject(error);
//   }
// );

// ---------------------------------------------------------------------------
// Schedule/targets cache (frontend)
// Makes Schedule tab feel instant by avoiding repeat network waits.
// ---------------------------------------------------------------------------
const scheduleTargetsCache = new Map(); // key -> { expiresAt, data }
const scheduleTargetsInFlight = new Map(); // key -> Promise<axiosResponse>
const SCHEDULE_TARGETS_CACHE_TTL_MS = 60 * 1000;
const getScheduleTargetsCacheKey = (practiceId) => `practice:${practiceId}`;

// ---------------------------------------------------------------------------
// Practice patients cache (frontend, short-lived session cache with TTL)
// Avoids duplicate parallel fetches while ensuring fresh data on mutations.
// ---------------------------------------------------------------------------
const practicePatientsCache = new Map(); // key -> { timestamp, data }
const practicePatientsInFlight = new Map(); // key -> Promise<axiosResponse>
const PRACTICE_PATIENTS_CACHE_TTL_MS = 10 * 1000; // 10 seconds TTL
const normalizeParams = (params = {}) => {
  return Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      // Exclude cache-control flags from cache key
      if (key === 'skipCache' || key === 'forceRefresh') return acc;
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
};
const PRACTICE_PATIENTS_CACHE_VERSION = 'v11';
const getPracticePatientsCacheKey = (practiceId, params = {}) =>
  `${PRACTICE_PATIENTS_CACHE_VERSION}:practice:${practiceId}:patients:${JSON.stringify(normalizeParams(params))}`;

const practiceService = {
  // Get all practices
  getPractices: async (params = {}) => {
    const response = await api.get('/practices', { params });
    return response;
  },

  // Get practice by ID
  getPractice: async (id) => {
    const response = await api.get(`/practices/${id}`);
    return response;
  },

  // Check practice admin username availability
  checkAdminUsername: async (username, practiceId = null) => {
    const params = { username };
    if (practiceId) params.practiceId = practiceId;
    const response = await api.get('/practices/check-admin-username', { params });
    return response;
  },

  checkAdminEmail: async (email, practiceId = null) => {
    const params = { email };
    if (practiceId) params.practiceId = practiceId;
    const response = await api.get('/practices/check-admin-email', { params });
    return response;
  },

  // Get lightweight practice summary (faster dashboard load)
  getPracticeSummary: async (id) => {
    const response = await api.get(`/practices/${id}/summary`);
    return response;
  },

  // Create new practice
  createPractice: async (practiceData) => {
    // Check if practiceData is FormData (file upload) or regular object
    const isFormData = practiceData instanceof FormData;
    const config = isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};

    const response = await api.post('/practices', practiceData, config);
    return response;
  },

  // Update practice
  updatePractice: async (id, practiceData) => {
    // Check if practiceData is FormData (file upload) or regular object
    const isFormData = practiceData instanceof FormData;
    const config = isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};

    const response = await api.put(`/practices/${id}`, practiceData, config);
    return response;
  },

  // Delete practice
  deletePractice: async (id) => {
    const response = await api.delete(`/practices/${id}`);
    return response;
  },

  // Get practice dashboard data
  getPracticeDashboard: async (id) => {
    const response = await api.get(`/practices/${id}/dashboard`);
    return response;
  },

  // Get practice statistics
  getPracticeStats: async (id) => {
    const response = await api.get(`/practices/${id}/stats`);
    return response;
  },

  // Get practice patients
  getPracticePatients: async (id, params = {}) => {
    const cacheKey = getPracticePatientsCacheKey(id, params);
    if (!params?.skipCache && !params?.forceRefresh) {
      const cached = practicePatientsCache.get(cacheKey);
      if (cached) {
        if (Date.now() - (cached.timestamp || 0) < PRACTICE_PATIENTS_CACHE_TTL_MS) {
          return { data: cached.data };
        }
        practicePatientsCache.delete(cacheKey);
      }
    }

    const inFlight = practicePatientsInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const requestStartedAt = performance.now();
    const promise = api.get(`/practices/${id}/patients`, { params }).then((response) => {
      const elapsedMs = performance.now() - requestStartedAt;
      const serverTimingMs = response?.headers?.['x-patients-timing-ms'];
      const serverDbMs = response?.headers?.['x-patients-db-ms'];
      const payloadBytes = response?.headers?.['x-patients-rows-bytes'];
      console.info(
        `[practiceService.getPracticePatients] practice=${id} page=${params?.page ?? 1} limit=${params?.limit ?? 'default'} ` +
        `client_ms=${elapsedMs.toFixed(1)} server_ms=${serverTimingMs ?? 'n/a'} db_ms=${serverDbMs ?? 'n/a'} rows_bytes=${payloadBytes ?? 'n/a'}`
      );

      practicePatientsCache.set(cacheKey, { timestamp: Date.now(), data: response.data });
      practicePatientsInFlight.delete(cacheKey);
      return response;
    }).catch((err) => {
      practicePatientsInFlight.delete(cacheKey);
      throw err;
    });

    practicePatientsInFlight.set(cacheKey, promise);
    return promise;
  },

  // Invalidate cached patient lists for one practice or all practices.
  invalidatePracticePatientsCache: (practiceId = null) => {
    if (!practiceId) {
      practicePatientsCache.clear();
      practicePatientsInFlight.clear();
      return;
    }
    const needle = `:practice:${practiceId}:patients:`;
    Array.from(practicePatientsCache.keys()).forEach((key) => {
      if (key.includes(needle)) {
        practicePatientsCache.delete(key);
      }
    });
    Array.from(practicePatientsInFlight.keys()).forEach((key) => {
      if (key.includes(needle)) {
        practicePatientsInFlight.delete(key);
      }
    });
  },

  // Get practice providers (optional params e.g. { minimal: true } for dropdowns — id, name, role only)
  getPracticeProviders: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/providers`, { params });
    return response;
  },

  // Get practice caregivers (optional params e.g. { minimal: true })
  getPracticeCaregivers: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/caregivers`, { params });
    return response;
  },

  // Search practices
  searchPractices: async (query, filters = {}) => {
    const response = await api.get('/practices/search', {
      params: { q: query, ...filters }
    });
    return response;
  },

  // Get practice recent uploads
  getRecentUploads: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/recent-uploads`, { params });
    return response;
  },

  // Get practice missed uploads
  getMissedUploads: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/missed-uploads`, { params });
    return response;
  },

  // Get practice abnormal measurements
  getAbnormalMeasurements: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/abnormal-measurements`, { params });
    return response;
  },

  // Get practice billing reports
  getBillingReports: async (id, params = {}) => {
    const response = await api.get(`/practices/${id}/billing-reports`, { params });
    return response;
  },

  // Update practice settings
  updatePracticeSettings: async (id, settings) => {
    const response = await api.put(`/practices/${id}/settings`, settings);
    return response;
  },

  // Get practice schedule and targets
  getScheduleAndTargets: async (id) => {
    const cacheKey = getScheduleTargetsCacheKey(id);
    const now = Date.now();

    const cached = scheduleTargetsCache.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      // Keep the same response shape used by the UI: response.data.success + response.data.data
      return { data: cached.data };
    }

    const inFlight = scheduleTargetsInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const promise = api.get(`/practices/${id}/schedule-targets`).then((response) => {
      scheduleTargetsCache.set(cacheKey, {
        expiresAt: Date.now() + SCHEDULE_TARGETS_CACHE_TTL_MS,
        data: response.data
      });
      scheduleTargetsInFlight.delete(cacheKey);
      return response;
    }).catch((err) => {
      scheduleTargetsInFlight.delete(cacheKey);
      throw err;
    });

    scheduleTargetsInFlight.set(cacheKey, promise);
    return promise;
  },

  // Update practice schedule and targets
  updateScheduleAndTargets: async (id, data) => {
    const cacheKey = getScheduleTargetsCacheKey(id);
    const response = await api.put(`/practices/${id}/schedule-targets`, data);
    // Invalidate so the next Schedule load is fresh.
    scheduleTargetsCache.delete(cacheKey);
    return response;
  },

  // Import patients to practice
  importPatients: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/practices/${id}/import-patients`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Export practice data
  exportPracticeData: async (id, format = 'csv') => {
    const response = await api.get(`/practices/${id}/export`, {
      params: { format },
      responseType: 'blob'
    });
    return response;
  },

  // Get practice admins
  getPracticeAdmins: async (id) => {
    const response = await api.get(`/practices/${id}/admins`);
    return response;
  },

  // Get system caregivers (optionally filtered by practiceId). High limit so pickers include all assignable users.
  getSystemCaregivers: async (practiceId = null, extraParams = {}) => {
    const params = { page: 1, limit: 2500, ...extraParams };
    if (practiceId) params.practiceId = practiceId;
    const response = await api.get('/system-caregivers', { params });
    return response;
  }
};

export default practiceService;
